import { useEffect, useState } from 'react'
import { Users, UserPlus, IdCard, Package } from 'lucide-react'
import { PageHeader } from '@/components/ui/PageHeader'
import { Badge } from '@/components/ui/Badge'
import { toast } from '@/components/ui/Toast'
import { useAuthStore } from '@/stores/authStore'
import { MyCompanyService, type CompanyDashboard } from '@/services/modules/MyCompanyService'

export function MyCompanyDashboardPage() {
  const user = useAuthStore(s => s.user)
  const [data, setData] = useState<CompanyDashboard | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    setLoading(true)
    MyCompanyService.dashboard()
      .then(setData)
      .catch(() => toast.error('Erro ao carregar o dashboard.'))
      .finally(() => setLoading(false))
  }, [])

  const activeCollaborators = data?.activeCollaborators ?? 0
  const activeDependents = data?.activeDependents ?? 0
  const cards = [
    { label: 'Funcionários ativos', value: activeCollaborators, icon: Users,    color: 'text-emerald-600', ring: 'ring-emerald-200', bg: 'bg-emerald-50' },
    { label: 'Dependentes ativos',  value: activeDependents,    icon: UserPlus, color: 'text-brand-600',   ring: 'ring-brand-200',   bg: 'bg-brand-50' },
    { label: 'Total de ativos',     value: activeCollaborators + activeDependents, icon: IdCard, color: 'text-violet-600', ring: 'ring-violet-200', bg: 'bg-violet-50' },
  ]

  // Total de assinaturas ativas (titulares) para o cálculo de porcentagem.
  const planTypeTotal = (data?.byPlanType ?? []).reduce((acc, r) => acc + r.total, 0)
  const pct = (n: number) => planTypeTotal > 0
    ? `${((n / planTypeTotal) * 100).toLocaleString('pt-BR', { maximumFractionDigits: 2 })}%`
    : '0%'

  return (
    <>
      <PageHeader title="Dashboard" subtitle={user?.name ?? 'Painel da empresa'}
        crumbs={[{ label: 'Minha empresa' }, { label: 'Dashboard' }]}
      />

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
        {cards.map(c => (
          <div key={c.label} className={`rounded-md ring-1 ${c.ring} ${c.bg} p-5`}>
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-gray-500">{c.label}</span>
              <c.icon size={18} className={c.color} />
            </div>
            <div className={`mt-3 text-3xl font-bold ${c.color}`}>{loading ? '—' : c.value}</div>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-md ring-1 ring-gray-200 p-4 sm:p-5">
        <div className="flex items-center gap-2 mb-4">
          <Package size={16} className="text-brand-500" />
          <h2 className="text-sm font-semibold text-gray-800">Assinaturas por tipo de plano</h2>
        </div>
        {loading ? (
          <div className="py-8 text-center text-sm text-gray-400">Carregando</div>
        ) : !data?.byPlanType.length ? (
          <div className="py-8 text-center text-sm text-gray-400">Nenhuma assinatura ativa.</div>
        ) : (
          <ul className="divide-y divide-gray-100">
            {data.byPlanType.map(r => (
              <li key={r.planType} className="flex items-center justify-between py-2.5">
                <span className="text-sm text-gray-700">{r.planType}</span>
                <span className="flex items-center gap-2">
                  <span className="text-xs text-gray-400">{pct(r.total)}</span>
                  <Badge variant="blue">{r.total}</Badge>
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </>
  )
}
